import React, { useEffect, useState, useCallback } from 'react';
import { 
  FiFileText, 
  FiUsers, 
  FiEye, 
  FiTrendingUp, 
  FiEdit, 
  FiTrash2, 
  FiPlus,
  FiActivity,
  FiMonitor,
  FiSmartphone,
  FiTablet,
  FiGlobe,
  FiMousePointer
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import Loader from '../../components/common/Loader/Loader';
import articleService from '../../services/articleService';
import analyticsService from '../../services/analyticsService';
import { Article } from '../../types/article.types';
import { TrafficSource} from '../../types/article.types'
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import styles from './Dashboard.module.css';

interface StatsCardProps {
  title: string;
  value: string | number;
  icon: React. ReactNode;
  color: string;
  trend?: string;
  trendValue?: number;
}

interface AnalyticsData {
  realtime: {
    activeUsers:  number;
    timestamp: string;
  };
  overview: {
    totalUsers: number;
    totalSessions: number;
    totalPageViews: number;
    averageSessionDuration: number;
    bounceRate: number;
    newUsers: number;
  };
  dailyStats: Array<{
    date: string;
    users: number;
    sessions: number;
    pageViews: number;
    bounceRate: number;
  }>;
  trafficSources: Array<{
    source: string;
    users: number;
    percentage: number;
  }>;
  deviceStats: Array<{
    category: string;
    users: number;
    percentage: number;
  }>;
  topPages: Array<{
    path: string;
    views: number;
    uniqueUsers: number;
    averageTime: number;
  }>;
  topCountries: Array<{
    country: string;
    users:  number;
    percentage: number;
  }>;
  engagement:  {
    totalEvents: number;
    eventsPerSession: number;
    engagementRate: number;
  };
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const StatsCard: React.FC<StatsCardProps> = ({ title, value, icon, color, trend, trendValue }) => (
  <Card className={styles.statsCard}>
    <div className={styles.statsCardContent}>
      <div className={styles.statsCardLeft}>
        <p className={styles.statsTitle}>{title}</p>
        <h2 className={styles.statsValue}>{value}</h2>
        {trend && (
          <p className={`${styles.statsTrend} ${trendValue && trendValue < 0 ? styles.trendNegative : ''}`}>
            {trendValue && trendValue > 0 ? '↑' : trendValue && trendValue < 0 ? '↓' :  ''} {trend}
          </p>
        )}
      </div>
      <div className={styles.statsCardIcon} style={{ backgroundColor: color }}>
        {icon}
      </div>
    </div>
  </Card>
);

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [realtimeUsers, setRealtimeUsers] = useState<number>(0);

  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    try {
      setAnalyticsLoading(true);
      const response = await analyticsService.getDashboard();
      setAnalyticsData(response. data);
      setRealtimeUsers(response. data.realtime.activeUsers);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  // Fetch recent articles
  const fetchRecentArticles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await articleService. getArticles({
        limit: 5,
        sortBy: 'createdAt',
        order:  'desc',
      });
      setArticles(response.data?. articles || []);
    } catch (error) {
      console.error('Failed to fetch articles:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll realtime data every 30 seconds
  useEffect(() => {
    const pollRealtime = async () => {
      try {
        const response = await analyticsService.getRealtimeData();
        setRealtimeUsers(response.data. activeUsers);
      } catch (error) {
        console.error('Failed to fetch realtime data:', error);
      }
    };

    const interval = setInterval(pollRealtime, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchAnalytics();
    fetchRecentArticles();
  }, [fetchAnalytics, fetchRecentArticles]);

  // Calculate stats from analytics data
  const stats = analyticsData ? [
    {
      title: 'Total Page Views',
      value: analyticsData.overview.totalPageViews.toLocaleString(),
      icon: <FiEye />,
      color: '#3b82f6',
      trend: `${analyticsData.dailyStats. length > 1 
        ? ((analyticsData.dailyStats[analyticsData.dailyStats.length - 1]. pageViews / 
            analyticsData.dailyStats[analyticsData.dailyStats.length - 2].pageViews - 1) * 100).toFixed(1)
        : 0}% from yesterday`,
      trendValue: analyticsData.dailyStats.length > 1 
        ? (analyticsData.dailyStats[analyticsData.dailyStats. length - 1].pageViews / 
            analyticsData. dailyStats[analyticsData. dailyStats.length - 2].pageViews - 1) * 100
        : 0,
    },
    {
      title: 'Total Users',
      value: analyticsData.overview.totalUsers.toLocaleString(),
      icon: <FiUsers />,
      color: '#10b981',
      trend: `${analyticsData.overview.newUsers} new users`,
      trendValue: analyticsData.overview.newUsers,
    },
    {
      title: 'Active Now',
      value: realtimeUsers.toLocaleString(),
      icon: <FiActivity />,
      color: '#f59e0b',
      trend:  'Real-time',
    },
    {
      title:  'Engagement Rate',
      value: `${analyticsData.engagement.engagementRate.toFixed(1)}%`,
      icon: <FiTrendingUp />,
      color: '#8b5cf6',
      trend: `${analyticsData.engagement.eventsPerSession. toFixed(2)} events/session`,
      trendValue: analyticsData.engagement.engagementRate,
    },
  ] : [];

  // Format chart data
  const formatDailyStatsData = () => {
    if (!analyticsData?. dailyStats) return [];
    return analyticsData.dailyStats. map(stat => ({
      date: new Date(stat.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      users: stat.users,
      sessions: stat.sessions,
      pageViews: stat.pageViews,
      bounceRate: stat. bounceRate,
    }));
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className={styles.chartTooltip}>
          <p className={styles.tooltipLabel}>{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (analyticsLoading) {
    return (
      <div className={styles.dashboard}>
        <div className={styles.loaderContainer}>
          <Loader size="large" />
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles. dashboard}>
      {/* Welcome Section */}
      <div className={styles.welcomeSection}>
        <div>
          <h1 className={styles.welcomeTitle}>
            Welcome back, {user?.name}!  👋
          </h1>
          <p className={styles.welcomeSubtitle}>
            Here's what's happening with your news platform today. 
          </p>
          <div className={styles.realtimeBadge}>
            <FiActivity className={styles.pulseIcon} />
            <span>{realtimeUsers} users active now</span>
          </div>
        </div>
        <Button
          variant="primary"
          icon={<FiPlus />}
          onClick={() => navigate('/articles/create')}
        >
          Create Article
        </Button>
      </div>

      {/* Stats Grid */}
      <div className={styles.statsGrid}>
        {stats.map((stat, index) => (
          <StatsCard key={index} {...stat} />
        ))}
      </div>

      {/* Charts Row 1: Traffic & Engagement Trends */}
      <div className={styles.chartsRow}>
        {/* Traffic Overview - Area Chart */}
        <Card 
          title="Traffic Overview" 
          subtitle="Daily users, sessions, and page views"
          className={styles.chartCard}
        >
          <div className={styles.chartContainer}>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={formatDailyStatsData()}>
                <defs>
                  <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorPageViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="date" 
                  stroke="#6b7280"
                  style={{ fontSize: '0.75rem' }}
                />
                <YAxis 
                  stroke="#6b7280"
                  style={{ fontSize: '0.75rem' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  wrapperStyle={{ fontSize: '0.875rem' }}
                  iconType="circle"
                />
                <Area
                  type="monotone"
                  dataKey="users"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorUsers)"
                  name="Users"
                />
                <Area
                  type="monotone"
                  dataKey="sessions"
                  stroke="#10b981"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorSessions)"
                  name="Sessions"
                />
                <Area
                  type="monotone"
                  dataKey="pageViews"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorPageViews)"
                  name="Page Views"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Bounce Rate Trend - Line Chart */}
        <Card 
          title="Bounce Rate Trend" 
          subtitle="Daily bounce rate percentage"
          className={styles.chartCard}
        >
          <div className={styles.chartContainer}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={formatDailyStatsData()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="date" 
                  stroke="#6b7280"
                  style={{ fontSize:  '0.75rem' }}
                />
                <YAxis 
                  stroke="#6b7280"
                  style={{ fontSize: '0.75rem' }}
                  domain={[0, 100]}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  wrapperStyle={{ fontSize: '0.875rem' }}
                  iconType="circle"
                />
                <Line
                  type="monotone"
                  dataKey="bounceRate"
                  stroke="#ef4444"
                  strokeWidth={3}
                  dot={{ fill: '#ef4444', r: 5 }}
                  activeDot={{ r: 7 }}
                  name="Bounce Rate %"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Charts Row 2: Sources & Devices */}
      <div className={styles.chartsRow}>
        {/* Traffic Sources - Pie Chart */}
        <Card 
          title="Traffic Sources" 
          subtitle="User acquisition channels"
          className={styles. chartCard}
        >
          <div className={styles.chartContainer}>
  <ResponsiveContainer width="100%" height={300}>
  <PieChart>
    <Pie
      data={analyticsData?.trafficSources || []}
      cx="50%"
      cy="50%"
      labelLine={false}
      label={({ payload }) =>
        payload
          ? `${payload.source}: ${payload.percentage.toFixed(1)}%`
          : ''
      }
      outerRadius={100}
      fill="#8884d8"
      dataKey="users"
      nameKey="source"
    >
      {analyticsData?.trafficSources?.map((_, index) => (
        <Cell
          key={`cell-${index}`}
          fill={COLORS[index % COLORS.length]}
        />
      ))}
    </Pie>
    <Tooltip content={<CustomTooltip />} />
  </PieChart>
</ResponsiveContainer>


  <div className={styles.pieChartLegend}>
    {analyticsData?.trafficSources?.map((source, index) => (
      <div key={index} className={styles.legendItem}>
        <span
          className={styles.legendColor}
          style={{ backgroundColor: COLORS[index % COLORS.length] }}
        />
        <span className={styles.legendLabel}>
          {source.source}: {source.users.toLocaleString()} (
          {source.percentage.toFixed(1)}%)
        </span>
      </div>
    ))}
  </div>
</div>

        </Card>

        {/* Device Categories - Bar Chart */}
        <Card 
          title="Device Categories" 
          subtitle="Users by device type"
          className={styles.chartCard}
        >
          <div className={styles. chartContainer}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analyticsData?.deviceStats || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="category" 
                  stroke="#6b7280"
                  style={{ fontSize: '0.75rem' }}
                />
                <YAxis 
                  stroke="#6b7280"
                  style={{ fontSize: '0.75rem' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  wrapperStyle={{ fontSize: '0.875rem' }}
                  iconType="circle"
                />
                <Bar 
                  dataKey="users" 
                  fill="#3b82f6" 
                  radius={[8, 8, 0, 0]}
                  name="Users"
                />
              </BarChart>
            </ResponsiveContainer>
            <div className={styles.deviceStats}>
              {analyticsData?. deviceStats.map((device, index) => (
                <div key={index} className={styles.deviceStatItem}>
                  {device.category === 'desktop' && <FiMonitor />}
                  {device.category === 'mobile' && <FiSmartphone />}
                  {device.category === 'tablet' && <FiTablet />}
                  <span>{device.category}:  {device.percentage.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* Charts Row 3: Top Pages & Countries */}
      <div className={styles.chartsRow}>
        {/* Top Pages */}
        <Card 
          title="Top Pages" 
          subtitle="Most viewed content"
          className={styles.chartCard}
        >
          <div className={styles.topPagesContainer}>
            {analyticsData?.topPages.map((page, index) => (
              <div key={index} className={styles.topPageItem}>
                <div className={styles.topPageRank}>{index + 1}</div>
                <div className={styles.topPageContent}>
                  <div className={styles.topPagePath}>{page.path}</div>
                  <div className={styles.topPageStats}>
                    <span><FiEye /> {page.views.toLocaleString()} views</span>
                    <span><FiUsers /> {page.uniqueUsers.toLocaleString()} users</span>
                    <span><FiActivity /> {Math.round(page.averageTime)}s avg</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Top Countries */}
        <Card 
          title="Top Countries" 
          subtitle="Geographic distribution"
          className={styles. chartCard}
        >
          <div className={styles.topCountriesContainer}>
            {analyticsData?.topCountries.map((country, index) => (
              <div key={index} className={styles.countryItem}>
                <div className={styles.countryInfo}>
                  <FiGlobe className={styles.countryIcon} />
                  <span className={styles.countryName}>{country.country}</span>
                </div>
                <div className={styles. countryStats}>
                  <span className={styles.countryUsers}>
                    {country.users. toLocaleString()} users
                  </span>
                  <div className={styles.countryBar}>
                    <div 
                      className={styles.countryBarFill}
                      style={{ width: `${country.percentage}%` }}
                    />
                  </div>
                  <span className={styles.countryPercentage}>
                    {country.percentage. toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Recent Articles */}
      <Card title="Recent Articles" subtitle="Your latest published articles">
        {loading ? (
          <div className={styles.loaderContainer}>
            <Loader size="medium" />
          </div>
        ) : articles.length === 0 ? (
          <div className={styles.emptyState}>
            <FiFileText className={styles.emptyIcon} />
            <h3>No articles yet</h3>
            <p>Start creating your first article to see it here</p>
            <Button
              variant="primary"
              icon={<FiPlus />}
              onClick={() => navigate('/articles/create')}
            >
              Create Article
            </Button>
          </div>
        ) : (
          <div className={styles.articlesTable}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Views</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((article) => (
                  <tr key={article.id}>
                    <td>
                      <div className={styles.articleTitle}>
                        <img
                          src={article.featuredImage. thumbnailUrl || article.featuredImage.url}
                          alt={article.headline}
                          className={styles. articleThumbnail}
                        />
                        <span>{article.headline}</span>
                      </div>
                    </td>
                    <td>
                      <span className={styles.categoryBadge}>{article.category}</span>
                    </td>
                    <td>
                      <span
                        className={`${styles.statusBadge} ${
                          article.status === 'published'
                            ? styles.statusPublished
                            : styles.statusDraft
                        }`}
                      >
                        {article.status}
                      </span>
                    </td>
                    <td>{article.views.toLocaleString()}</td>
                    <td>{new Date(article.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          className={styles.actionButton}
                          onClick={() => navigate(`/articles/edit/${article.id}`)}
                          title="Edit"
                        >
                          <FiEdit />
                        </button>
                        <button
                          className={`${styles.actionButton} ${styles.deleteButton}`}
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Quick Actions */}
      <div className={styles.quickActions}>
        <Card title="Quick Actions" padding="medium">
          <div className={styles.quickActionsGrid}>
            <button
              className={styles.quickActionButton}
              onClick={() => navigate('/articles/create')}
            >
              <FiFileText />
              <span>New Article</span>
            </button>
            <button
              className={styles.quickActionButton}
              onClick={() => navigate('/team')}
            >
              <FiUsers />
              <span>Manage Team</span>
            </button>
            <button
              className={styles.quickActionButton}
              onClick={() => navigate('/analytics')}
            >
              <FiTrendingUp />
              <span>View Analytics</span>
            </button>
            <button
              className={styles. quickActionButton}
              onClick={() => fetchAnalytics()}
            >
              <FiActivity />
              <span>Refresh Data</span>
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;